import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { brl, fmtData } from '../lib/format';
import {
  ImovelDetalhe as TImovelDetalhe,
  Inquilino,
  PrioridadeTarefa,
  Promissoria,
  PromissoriaStatus,
  GarantiaTipo,
  GarantiaStatus,
  Tarefa
} from '../types';
import { Modal } from '../components/Modal';
import { ImovelForm, statusBadge } from './Imoveis';

const garantiaTipos: { v: GarantiaTipo; l: string }[] = [
  { v: 'calcao_dinheiro', l: 'Calção em dinheiro' },
  { v: 'fiador',          l: 'Fiador' },
  { v: 'seguro_fianca',   l: 'Seguro-fiança' },
  { v: 'capitalizacao',   l: 'Título de capitalização' },
  { v: 'sem_garantia',    l: 'Sem garantia' }
];

export function ImovelDetalhe() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [data, setData] = useState<TImovelDetalhe | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [inqOpen, setInqOpen] = useState(false);
  const [inqEdit, setInqEdit] = useState<Inquilino | undefined>();
  const [reajusteOpen, setReajusteOpen] = useState(false);
  const [editandoLink, setEditandoLink] = useState(false);
  const [linkInput, setLinkInput] = useState('');
  const [tarefas, setTarefas] = useState<Tarefa[]>([]);
  const [tarefaOpen, setTarefaOpen] = useState(false);
  const [tarefaEdit, setTarefaEdit] = useState<Tarefa | undefined>();
  const [promissorias, setPromissorias] = useState<Promissoria[]>([]);
  const [promOpen, setPromOpen] = useState(false);
  const [promEdit, setPromEdit] = useState<Promissoria | undefined>();
  const [promLoteOpen, setPromLoteOpen] = useState(false);

  const carregar = () => api.get<TImovelDetalhe>(`/imoveis/${id}`).then(setData);
  const carregarTarefas = () => api.get<Tarefa[]>(`/tarefas?imovel_id=${id}`).then(setTarefas);
  const carregarPromissorias = () => api.get<Promissoria[]>(`/promissorias?imovel_id=${id}`).then(setPromissorias);
  useEffect(() => {
    if (id) { carregar(); carregarTarefas(); carregarPromissorias(); }
  }, [id]);

  const removerImovel = async () => {
    if (!confirm('Remover este imóvel? Todos os recebimentos, despesas e inquilinos relacionados serão apagados.')) return;
    await api.del(`/imoveis/${id}`);
    nav('/imoveis');
  };

  const removerInquilino = async (inq: Inquilino) => {
    if (!confirm(`Remover inquilino ${inq.nome}?`)) return;
    await api.del(`/inquilinos/${inq.id}`);
    carregar();
  };

  const salvarLink = async () => {
    if (!id) return;
    await api.put(`/contratos/${id}`, { url: linkInput || null });
    setEditandoLink(false);
    carregar();
  };

  const toggleTarefa = async (t: Tarefa) => {
    await api.put(`/tarefas/${t.id}`, { ...t, feito: t.feito ? 0 : 1 });
    carregarTarefas();
  };

  const removerTarefa = async (t: Tarefa) => {
    if (!confirm(`Remover tarefa "${t.titulo}"?`)) return;
    await api.del(`/tarefas/${t.id}`);
    carregarTarefas();
  };

  const removerPromissoria = async (p: Promissoria) => {
    if (!confirm(`Remover promissória "${p.descricao}${p.numero ? ` ${p.numero}/${p.total}` : ''}"?`)) return;
    await api.del(`/promissorias/${p.id}`);
    carregarPromissorias();
  };

  const registrarPagamentoPromissoria = async (p: Promissoria) => {
    const restante = p.valor - p.valor_pago;
    const valor = prompt(
      `Valor do pagamento (em aberto: ${brl(restante)}):`,
      String(restante.toFixed(2))
    );
    if (!valor) return;
    const novoPago = +(p.valor_pago + Number(valor)).toFixed(2);
    await api.put(`/promissorias/${p.id}`, { ...p, valor_pago: novoPago });
    carregarPromissorias();
  };

  if (!data) return <div className="text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Link to="/imoveis" className="text-sm text-slate-500 hover:text-slate-900">‹ Imóveis</Link>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={() => setEditOpen(true)}>Editar</button>
          <button className="btn-danger" onClick={removerImovel}>Remover</button>
        </div>
      </div>

      <div className="card-pad">
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0">
            <h1 className="text-xl font-bold">{data.apelido}</h1>
            <p className="text-slate-500 text-sm">{data.endereco}</p>
          </div>
          <span className={statusBadge(data.status).badge}>{statusBadge(data.status).l.toLowerCase()}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
          <Info l="Tipo" v={data.tipo} />
          <Info l="Dono" v={data.dono} />
          <Info l="Aluguel" v={brl(data.valor_aluguel)} />
          <Info l="Vencimento" v={`Dia ${data.dia_vencimento}`} />
        </div>
        {data.status === 'desocupado' && data.desocupado_desde && (
          <p className="mt-3 text-sm text-yellow-700">
            Desocupado desde {fmtData(data.desocupado_desde)}
            {(() => {
              const dias = Math.floor((Date.now() - new Date(data.desocupado_desde).getTime()) / (1000 * 60 * 60 * 24));
              return dias > 0 ? ` · ${dias} dia${dias > 1 ? 's' : ''}` : '';
            })()}
          </p>
        )}
        {data.observacoes && <p className="mt-3 text-sm text-slate-600 italic whitespace-pre-wrap">{data.observacoes}</p>}
      </div>

      {/* Contrato (link externo) */}
      <div className="card-pad">
        <div className="flex justify-between items-center mb-2">
          <h2 className="font-semibold">Contrato</h2>
          {!editandoLink && (
            <button
              className="text-xs text-brand-600 hover:underline"
              onClick={() => { setLinkInput(data.contrato_pdf ?? ''); setEditandoLink(true); }}
            >
              {data.contrato_pdf ? 'editar link' : 'adicionar link'}
            </button>
          )}
        </div>
        {editandoLink ? (
          <div className="space-y-2">
            <input
              type="url"
              className="input"
              placeholder="https://drive.google.com/... ou Dropbox/OneDrive"
              value={linkInput}
              onChange={(e) => setLinkInput(e.target.value)}
              autoFocus
            />
            <div className="flex gap-2">
              <button className="btn-primary" onClick={salvarLink}>Salvar</button>
              <button className="btn-secondary" onClick={() => setEditandoLink(false)}>Cancelar</button>
              {data.contrato_pdf && (
                <button
                  className="btn-ghost text-red-600"
                  onClick={() => { setLinkInput(''); }}
                >
                  Limpar
                </button>
              )}
            </div>
            <p className="text-xs text-slate-500">
              Cole a URL do PDF (Google Drive, Dropbox, OneDrive, etc).
            </p>
          </div>
        ) : data.contrato_pdf ? (
          <a
            href={data.contrato_pdf}
            target="_blank"
            rel="noreferrer"
            className="btn-secondary inline-flex max-w-full"
          >
            <span className="truncate">📄 Abrir contrato</span>
          </a>
        ) : (
          <p className="text-sm text-slate-500">Nenhum link cadastrado.</p>
        )}
      </div>

      {/* Tarefas */}
      <div className="card-pad">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-semibold">Tarefas</h2>
          <button className="btn-primary text-xs" onClick={() => { setTarefaEdit(undefined); setTarefaOpen(true); }}>+ Nova</button>
        </div>
        {tarefas.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhuma tarefa.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {tarefas.map((t) => (
              <li key={t.id} className="py-2 flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={!!t.feito}
                  onChange={() => toggleTarefa(t)}
                  className="mt-1 h-4 w-4 cursor-pointer"
                />
                <div className="min-w-0 flex-1">
                  <div className={`font-medium ${t.feito ? 'line-through text-slate-400' : ''}`}>{t.titulo}</div>
                  <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-0.5">
                    <PrioridadeBadge p={t.prioridade} />
                    {t.prazo && <span>prazo {fmtData(t.prazo)}</span>}
                    {t.observacao && <span className="italic">· {t.observacao}</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button className="text-xs text-brand-600 hover:underline" onClick={() => { setTarefaEdit(t); setTarefaOpen(true); }}>editar</button>
                  <button className="text-xs text-red-600 hover:underline" onClick={() => removerTarefa(t)}>remover</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Inquilinos */}
      <div className="card-pad">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-semibold">Inquilinos</h2>
          <button className="btn-primary text-xs" onClick={() => { setInqEdit(undefined); setInqOpen(true); }}>+ Novo</button>
        </div>
        {data.inquilinos.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhum inquilino registrado.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.inquilinos.map((inq) => {
              const garantiaLabel = garantiaTipos.find((g) => g.v === inq.garantia_tipo)?.l;
              return (
                <li key={inq.id} className="py-3 flex justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium flex items-center gap-2 flex-wrap">
                      {inq.nome}
                      {inq.ativo ? <span className="badge-green">atual</span> : <span className="badge-slate">histórico</span>}
                      {garantiaLabel && (
                        <span className={inq.garantia_status === 'retido' ? 'badge-yellow' : 'badge-slate'}>
                          {garantiaLabel}{inq.garantia_valor ? ` ${brl(inq.garantia_valor)}` : ''}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">
                      {fmtData(inq.inicio_contrato)} → {fmtData(inq.fim_contrato)}
                      {inq.contato && ` · ${inq.contato}`}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button className="text-xs text-brand-600 hover:underline" onClick={() => { setInqEdit(inq); setInqOpen(true); }}>editar</button>
                    <button className="text-xs text-red-600 hover:underline" onClick={() => removerInquilino(inq)}>remover</button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Promissórias */}
      <div className="card-pad">
        <div className="flex justify-between items-center mb-3 gap-2 flex-wrap">
          <h2 className="font-semibold">Promissórias</h2>
          <div className="flex gap-2">
            <button className="btn-secondary text-xs" onClick={() => setPromLoteOpen(true)}>+ Lote (parcelar)</button>
            <button className="btn-primary text-xs" onClick={() => { setPromEdit(undefined); setPromOpen(true); }}>+ Avulsa</button>
          </div>
        </div>
        {promissorias.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhuma promissória cadastrada.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {promissorias.map((p) => (
              <li key={p.id} className="py-3 flex justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2 flex-wrap">
                    <span className="truncate">{p.descricao}</span>
                    {p.numero && p.total && <span className="text-xs text-slate-500">{p.numero}/{p.total}</span>}
                    <PromissoriaBadge p={p} />
                  </div>
                  <div className="text-xs text-slate-500">
                    Vence {fmtData(p.vencimento)}
                    {p.valor_pago > 0 && ` · pago ${brl(p.valor_pago)} de ${brl(p.valor)}`}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-semibold">{brl(p.valor)}</div>
                  <div className="flex gap-2 justify-end mt-1 flex-wrap">
                    {p.status !== 'quitada' && p.status !== 'executada' && (
                      <button className="text-xs text-green-700 hover:underline" onClick={() => registrarPagamentoPromissoria(p)}>
                        registrar pgto
                      </button>
                    )}
                    <button className="text-xs text-brand-600 hover:underline" onClick={() => { setPromEdit(p); setPromOpen(true); }}>editar</button>
                    <button className="text-xs text-red-600 hover:underline" onClick={() => removerPromissoria(p)}>remover</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Reajustes */}
      <div className="card-pad">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-semibold">Reajustes</h2>
          <button className="btn-primary text-xs" onClick={() => setReajusteOpen(true)}>+ Aplicar</button>
        </div>
        {data.reajustes.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhum reajuste registrado.</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm">
            {data.reajustes.map((r) => (
              <li key={r.id} className="py-2 flex justify-between gap-2">
                <div>
                  <div className="font-medium">{fmtData(r.data)}</div>
                  <div className="text-xs text-slate-500">
                    {brl(r.valor_anterior)} → {brl(r.valor_novo)}
                    {r.indice && ` · ${r.indice.toUpperCase()}`}
                    {r.percentual != null && ` (${r.percentual.toFixed(2)}%)`}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {data && (
        <ImovelForm
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={carregar}
          imovel={data}
        />
      )}
      <InquilinoForm
        open={inqOpen}
        onClose={() => setInqOpen(false)}
        onSaved={carregar}
        imovelId={id!}
        inquilino={inqOpen ? inqEdit : undefined}
      />
      <ReajusteForm
        open={reajusteOpen}
        onClose={() => setReajusteOpen(false)}
        onSaved={carregar}
        imovelId={id!}
        valorAtual={data.valor_aluguel}
      />
      <TarefaForm
        open={tarefaOpen}
        onClose={() => setTarefaOpen(false)}
        onSaved={carregarTarefas}
        imovelId={id!}
        tarefa={tarefaOpen ? tarefaEdit : undefined}
      />
      <PromissoriaForm
        open={promOpen}
        onClose={() => setPromOpen(false)}
        onSaved={carregarPromissorias}
        imovelId={id!}
        inquilinoAtivoId={data.inquilinos.find((i) => i.ativo)?.id ?? null}
        promissoria={promOpen ? promEdit : undefined}
      />
      <PromissoriaLoteForm
        open={promLoteOpen}
        onClose={() => setPromLoteOpen(false)}
        onSaved={carregarPromissorias}
        imovelId={id!}
        inquilinoAtivoId={data.inquilinos.find((i) => i.ativo)?.id ?? null}
        valorAluguel={data.valor_aluguel}
      />
    </div>
  );
}

function PromissoriaBadge({ p }: { p: Promissoria }) {
  if (p.status === 'quitada') return <span className="badge-green">quitada</span>;
  if (p.status === 'executada') return <span className="badge-red">executada</span>;
  if (p.status === 'parcial') return <span className="badge-yellow">parcial</span>;
  // pendente
  const atrasada = new Date(p.vencimento) < new Date(new Date().toDateString());
  return atrasada ? <span className="badge-red">atrasada</span> : <span className="badge-slate">pendente</span>;
}

function PrioridadeBadge({ p }: { p: PrioridadeTarefa }) {
  const map: Record<PrioridadeTarefa, string> = {
    alta: 'badge-red',
    media: 'badge-yellow',
    baixa: 'badge-slate'
  };
  return <span className={map[p]}>{p}</span>;
}

interface PromProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  imovelId: string;
  inquilinoAtivoId: string | null;
  promissoria?: Promissoria;
}

function PromissoriaForm({ open, onClose, onSaved, imovelId, inquilinoAtivoId, promissoria }: PromProps) {
  const [form, setForm] = useState({
    descricao: '',
    numero: '' as number | '',
    total: '' as number | '',
    valor: 0,
    vencimento: new Date().toISOString().slice(0, 10),
    valor_pago: 0,
    status: 'pendente' as PromissoriaStatus,
    observacao: ''
  });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (promissoria) {
      setForm({
        descricao: promissoria.descricao,
        numero: promissoria.numero ?? '',
        total: promissoria.total ?? '',
        valor: promissoria.valor,
        vencimento: promissoria.vencimento.slice(0, 10),
        valor_pago: promissoria.valor_pago,
        status: promissoria.status,
        observacao: promissoria.observacao ?? ''
      });
    } else {
      setForm({
        descricao: '', numero: '', total: '',
        valor: 0,
        vencimento: new Date().toISOString().slice(0, 10),
        valor_pago: 0, status: 'pendente', observacao: ''
      });
    }
  }, [promissoria, open]);

  const submit = async () => {
    setSalvando(true);
    try {
      const payload = {
        imovel_id: imovelId,
        inquilino_id: inquilinoAtivoId,
        descricao: form.descricao,
        numero: form.numero === '' ? null : Number(form.numero),
        total: form.total === '' ? null : Number(form.total),
        valor: form.valor,
        vencimento: form.vencimento,
        valor_pago: form.valor_pago,
        status: form.status,
        observacao: form.observacao || null
      };
      if (promissoria) await api.put(`/promissorias/${promissoria.id}`, payload);
      else await api.post('/promissorias', payload);
      onSaved();
      onClose();
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={promissoria ? 'Editar promissória' : 'Nova promissória'}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={submit} disabled={salvando || !form.descricao || !form.valor}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label">Descrição</label>
          <input className="input" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} placeholder="ex: Acordo dívida 2026, Garantia contrato" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Número (opc.)</label>
            <input type="number" className="input" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value === '' ? '' : Number(e.target.value) })} placeholder="ex: 2" />
          </div>
          <div>
            <label className="label">Total (opc.)</label>
            <input type="number" className="input" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value === '' ? '' : Number(e.target.value) })} placeholder="ex: 5" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Valor</label>
            <input type="number" step="0.01" className="input" value={form.valor} onChange={(e) => setForm({ ...form, valor: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Vencimento</label>
            <input type="date" className="input" value={form.vencimento} onChange={(e) => setForm({ ...form, vencimento: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Valor já pago</label>
            <input type="number" step="0.01" className="input" value={form.valor_pago} onChange={(e) => setForm({ ...form, valor_pago: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Status</label>
            <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as PromissoriaStatus })}>
              <option value="pendente">Pendente (auto)</option>
              <option value="executada">Executada</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">Parcial/quitada são definidos automaticamente pelo valor pago.</p>
          </div>
        </div>
        <div>
          <label className="label">Observação</label>
          <textarea className="input" rows={2} value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
        </div>
      </div>
    </Modal>
  );
}

interface PromLoteProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  imovelId: string;
  inquilinoAtivoId: string | null;
  valorAluguel: number;
}

function PromissoriaLoteForm({ open, onClose, onSaved, imovelId, inquilinoAtivoId, valorAluguel }: PromLoteProps) {
  const [form, setForm] = useState({
    descricao: 'Garantia contrato',
    quantidade: 12,
    valor_unitario: valorAluguel,
    primeiro_vencimento: new Date().toISOString().slice(0, 10),
    observacao: ''
  });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, valor_unitario: valorAluguel }));
  }, [open, valorAluguel]);

  const submit = async () => {
    setSalvando(true);
    try {
      await api.post('/promissorias/lote', {
        imovel_id: imovelId,
        inquilino_id: inquilinoAtivoId,
        descricao: form.descricao,
        quantidade: form.quantidade,
        valor_unitario: form.valor_unitario,
        primeiro_vencimento: form.primeiro_vencimento,
        observacao: form.observacao || null
      });
      onSaved();
      onClose();
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Criar promissórias em lote"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={submit} disabled={salvando || form.quantidade < 1 || !form.valor_unitario}>
            {salvando ? 'Criando...' : `Criar ${form.quantidade}`}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-xs text-slate-500">
          Cria N promissórias com vencimentos mensais a partir da data informada.
          Útil pra parcelar uma dívida ou cadastrar um conjunto de garantia.
        </p>
        <div>
          <label className="label">Descrição</label>
          <input className="input" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} placeholder="ex: Acordo dívida 2026" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Quantidade</label>
            <input type="number" min={1} max={60} className="input" value={form.quantidade} onChange={(e) => setForm({ ...form, quantidade: Number(e.target.value) })} />
          </div>
          <div>
            <label className="label">Valor de cada</label>
            <input type="number" step="0.01" className="input" value={form.valor_unitario} onChange={(e) => setForm({ ...form, valor_unitario: Number(e.target.value) })} />
          </div>
        </div>
        <div>
          <label className="label">Primeiro vencimento</label>
          <input type="date" className="input" value={form.primeiro_vencimento} onChange={(e) => setForm({ ...form, primeiro_vencimento: e.target.value })} />
        </div>
        <div>
          <label className="label">Observação (opcional)</label>
          <textarea className="input" rows={2} value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
        </div>
        <div className="bg-slate-50 rounded-lg p-3 text-sm">
          <div>Total: <strong>{brl(form.quantidade * form.valor_unitario)}</strong></div>
        </div>
      </div>
    </Modal>
  );
}

interface TarefaProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  imovelId: string;
  tarefa?: Tarefa;
}

export function TarefaForm({ open, onClose, onSaved, imovelId, tarefa }: TarefaProps) {
  const [form, setForm] = useState({
    titulo: '',
    prazo: '',
    prioridade: 'media' as PrioridadeTarefa,
    observacao: ''
  });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (tarefa) {
      setForm({
        titulo: tarefa.titulo,
        prazo: tarefa.prazo ?? '',
        prioridade: tarefa.prioridade,
        observacao: tarefa.observacao ?? ''
      });
    } else {
      setForm({ titulo: '', prazo: '', prioridade: 'media', observacao: '' });
    }
  }, [tarefa, open]);

  const submit = async () => {
    setSalvando(true);
    try {
      const payload = {
        imovel_id: imovelId,
        titulo: form.titulo,
        prazo: form.prazo || null,
        prioridade: form.prioridade,
        observacao: form.observacao || null,
        feito: tarefa?.feito ?? 0
      };
      if (tarefa) await api.put(`/tarefas/${tarefa.id}`, payload);
      else await api.post('/tarefas', payload);
      onSaved();
      onClose();
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={tarefa ? 'Editar tarefa' : 'Nova tarefa'}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={submit} disabled={salvando || !form.titulo}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label">O que precisa ser feito</label>
          <input className="input" autoFocus value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="ex: Colocar placa de aluga-se" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Prioridade</label>
            <select className="input" value={form.prioridade} onChange={(e) => setForm({ ...form, prioridade: e.target.value as PrioridadeTarefa })}>
              <option value="alta">Alta</option>
              <option value="media">Média</option>
              <option value="baixa">Baixa</option>
            </select>
          </div>
          <div>
            <label className="label">Prazo (opcional)</label>
            <input type="date" className="input" value={form.prazo} onChange={(e) => setForm({ ...form, prazo: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label">Observação</label>
          <textarea className="input" rows={2} value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
        </div>
      </div>
    </Modal>
  );
}

function Info({ l, v }: { l: string; v: string }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{l}</div>
      <div className="font-medium">{v}</div>
    </div>
  );
}

interface InqProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  imovelId: string;
  inquilino?: Inquilino;
}

function InquilinoForm({ open, onClose, onSaved, imovelId, inquilino }: InqProps) {
  const [form, setForm] = useState({
    nome: '', contato: '', cpf: '',
    inicio_contrato: new Date().toISOString().slice(0, 10),
    fim_contrato: '', ativo: 1, observacoes: '',
    garantia_tipo: '' as GarantiaTipo | '',
    garantia_valor: '' as number | '',
    garantia_data_deposito: '',
    garantia_status: 'retido' as GarantiaStatus,
    garantia_data_devolucao: '',
    garantia_valor_devolvido: '' as number | '',
    garantia_observacao: ''
  });
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (inquilino) {
      setForm({
        nome: inquilino.nome,
        contato: inquilino.contato ?? '',
        cpf: inquilino.cpf ?? '',
        inicio_contrato: inquilino.inicio_contrato.slice(0, 10),
        fim_contrato: inquilino.fim_contrato?.slice(0, 10) ?? '',
        ativo: inquilino.ativo,
        observacoes: inquilino.observacoes ?? '',
        garantia_tipo: inquilino.garantia_tipo ?? '',
        garantia_valor: inquilino.garantia_valor ?? '',
        garantia_data_deposito: inquilino.garantia_data_deposito?.slice(0, 10) ?? '',
        garantia_status: inquilino.garantia_status ?? 'retido',
        garantia_data_devolucao: inquilino.garantia_data_devolucao?.slice(0, 10) ?? '',
        garantia_valor_devolvido: inquilino.garantia_valor_devolvido ?? '',
        garantia_observacao: inquilino.garantia_observacao ?? ''
      });
    } else {
      setForm({
        nome: '', contato: '', cpf: '',
        inicio_contrato: new Date().toISOString().slice(0, 10),
        fim_contrato: '', ativo: 1, observacoes: '',
        garantia_tipo: '', garantia_valor: '', garantia_data_deposito: '',
        garantia_status: 'retido', garantia_data_devolucao: '',
        garantia_valor_devolvido: '', garantia_observacao: ''
      });
    }
  }, [inquilino, open]);

  const submit = async () => {
    setSalvando(true);
    try {
      const payload = {
        ...form,
        imovel_id: imovelId,
        fim_contrato: form.fim_contrato || null,
        contato: form.contato || null,
        cpf: form.cpf || null,
        observacoes: form.observacoes || null,
        garantia_tipo: form.garantia_tipo || null,
        garantia_valor: form.garantia_valor === '' ? null : Number(form.garantia_valor),
        garantia_data_deposito: form.garantia_data_deposito || null,
        garantia_status: form.garantia_tipo ? form.garantia_status : null,
        garantia_data_devolucao: form.garantia_data_devolucao || null,
        garantia_valor_devolvido: form.garantia_valor_devolvido === '' ? null : Number(form.garantia_valor_devolvido),
        garantia_observacao: form.garantia_observacao || null
      };
      if (inquilino) await api.put(`/inquilinos/${inquilino.id}`, payload);
      else await api.post('/inquilinos', payload);
      onSaved();
      onClose();
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={inquilino ? 'Editar inquilino' : 'Novo inquilino'}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={submit} disabled={salvando || !form.nome}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label">Nome</label>
          <input className="input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Contato</label>
            <input className="input" value={form.contato} onChange={(e) => setForm({ ...form, contato: e.target.value })} />
          </div>
          <div>
            <label className="label">CPF</label>
            <input className="input" value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Início</label>
            <input type="date" className="input" value={form.inicio_contrato} onChange={(e) => setForm({ ...form, inicio_contrato: e.target.value })} />
          </div>
          <div>
            <label className="label">Fim</label>
            <input type="date" className="input" value={form.fim_contrato} onChange={(e) => setForm({ ...form, fim_contrato: e.target.value })} />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked ? 1 : 0 })} />
          Inquilino atual (desativa os anteriores)
        </label>
        <div>
          <label className="label">Observações</label>
          <textarea className="input" rows={2} value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
        </div>

        <div className="border-t border-slate-200 pt-3 mt-2">
          <h3 className="font-semibold text-sm mb-2">Garantia / Calção</h3>
          <div>
            <label className="label">Modalidade</label>
            <select
              className="input"
              value={form.garantia_tipo}
              onChange={(e) => setForm({ ...form, garantia_tipo: e.target.value as GarantiaTipo | '' })}
            >
              <option value="">— sem definir —</option>
              {garantiaTipos.map((g) => <option key={g.v} value={g.v}>{g.l}</option>)}
            </select>
          </div>
          {form.garantia_tipo && form.garantia_tipo !== 'sem_garantia' && (
            <div className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Valor depositado</label>
                  <input type="number" step="0.01" className="input"
                    value={form.garantia_valor}
                    onChange={(e) => setForm({ ...form, garantia_valor: e.target.value === '' ? '' : Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="label">Data do depósito</label>
                  <input type="date" className="input"
                    value={form.garantia_data_deposito}
                    onChange={(e) => setForm({ ...form, garantia_data_deposito: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="label">Status</label>
                <select className="input" value={form.garantia_status} onChange={(e) => setForm({ ...form, garantia_status: e.target.value as GarantiaStatus })}>
                  <option value="retido">Retida</option>
                  <option value="devolvido">Devolvida</option>
                  <option value="executado">Executada (descontada)</option>
                </select>
              </div>
              {form.garantia_status !== 'retido' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label">Data devolução/execução</label>
                    <input type="date" className="input"
                      value={form.garantia_data_devolucao}
                      onChange={(e) => setForm({ ...form, garantia_data_devolucao: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="label">Valor devolvido</label>
                    <input type="number" step="0.01" className="input"
                      value={form.garantia_valor_devolvido}
                      onChange={(e) => setForm({ ...form, garantia_valor_devolvido: e.target.value === '' ? '' : Number(e.target.value) })}
                    />
                  </div>
                </div>
              )}
              <div>
                <label className="label">Observação da garantia</label>
                <textarea className="input" rows={2} value={form.garantia_observacao} onChange={(e) => setForm({ ...form, garantia_observacao: e.target.value })} />
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

interface ReajProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  imovelId: string;
  valorAtual: number;
}

function ReajusteForm({ open, onClose, onSaved, imovelId, valorAtual }: ReajProps) {
  const hoje = new Date().toISOString().slice(0, 10);
  const umAnoAtras = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    data: hoje,
    indice: 'igpm' as 'igpm' | 'ipca' | 'manual',
    inicio: umAnoAtras,
    fim: hoje,
    percentual: 0,
    valor_novo: valorAtual,
    observacao: ''
  });
  const [calculando, setCalculando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (open) setForm((f) => ({ ...f, valor_novo: valorAtual, data: hoje }));
  }, [open, valorAtual]);

  const buscarIndice = async () => {
    setCalculando(true);
    try {
      const r = await api.get<{ percentual: number }>(
        `/indices?indice=${form.indice}&inicio=${form.inicio}&fim=${form.fim}`
      );
      const novo = +(valorAtual * (1 + r.percentual / 100)).toFixed(2);
      setForm((f) => ({ ...f, percentual: r.percentual, valor_novo: novo }));
    } catch (e) {
      alert('Erro ao buscar índice no Banco Central.');
    } finally {
      setCalculando(false);
    }
  };

  const submit = async () => {
    setSalvando(true);
    try {
      await api.post('/reajustes', {
        imovel_id: imovelId,
        data: form.data,
        valor_anterior: valorAtual,
        valor_novo: form.valor_novo,
        indice: form.indice,
        percentual: form.percentual || null,
        observacao: form.observacao || null
      });
      onSaved();
      onClose();
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Aplicar reajuste"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={submit} disabled={salvando || form.valor_novo <= 0}>
            {salvando ? 'Salvando...' : 'Aplicar'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label">Data do reajuste</label>
          <input type="date" className="input" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} />
        </div>
        <div>
          <label className="label">Índice</label>
          <select className="input" value={form.indice} onChange={(e) => setForm({ ...form, indice: e.target.value as any })}>
            <option value="igpm">IGP-M (FGV)</option>
            <option value="ipca">IPCA (IBGE)</option>
            <option value="manual">Manual</option>
          </select>
        </div>
        {form.indice !== 'manual' && (
          <div className="bg-slate-50 rounded-lg p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="label">Início</label>
                <input type="date" className="input" value={form.inicio} onChange={(e) => setForm({ ...form, inicio: e.target.value })} />
              </div>
              <div>
                <label className="label">Fim</label>
                <input type="date" className="input" value={form.fim} onChange={(e) => setForm({ ...form, fim: e.target.value })} />
              </div>
            </div>
            <button className="btn-secondary w-full" onClick={buscarIndice} disabled={calculando}>
              {calculando ? 'Buscando...' : `Buscar ${form.indice.toUpperCase()} no Banco Central`}
            </button>
            {form.percentual !== 0 && (
              <p className="text-sm text-slate-700">Acumulado: <strong>{form.percentual.toFixed(2)}%</strong></p>
            )}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Valor atual</label>
            <input className="input" disabled value={brl(valorAtual)} />
          </div>
          <div>
            <label className="label">Novo valor</label>
            <input type="number" step="0.01" className="input" value={form.valor_novo} onChange={(e) => setForm({ ...form, valor_novo: Number(e.target.value) })} />
          </div>
        </div>
        <div>
          <label className="label">Observação</label>
          <textarea className="input" rows={2} value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })} />
        </div>
      </div>
    </Modal>
  );
}
